import { Injectable } from '@angular/core';
import { MEAPIService } from '../api/me-api.service';
import { AsyncSubject, BehaviorSubject } from 'rxjs';
import { BasicAsyncResponseSuccessHandler, MEDoNothingResponseHandler, ResponseCode } from '../api/me-api-response';
import { FinanceTips } from './personal-finance-management-bean';
import { CacheKey, CacheService } from 'src/app/system/utility/cache.service';
import { Goal } from 'src/app/business/personal-finance-management/goals/goals';
import { CallbackParam, State } from 'src/app/business/personal-finance-management/personal-finance-management-bean';

@Injectable({
  providedIn: 'root'
})
export class PersonalFinanceManagementService {
  public state$: BehaviorSubject<CallbackParam> = new BehaviorSubject<CallbackParam>({newState: State.DEFAULT});

  private readonly API_PFM_GET_TIPS: string = MEAPIService.BASE_URL + '/customer/personalFinanceManagement/tips';
  private readonly API_PFM_GET_GOALS: string = MEAPIService.BASE_URL + '/customer/personalFinanceManagement/goals/list';
  private readonly API_PFM_DELETE_GOAL: string = MEAPIService.BASE_URL + '/customer/personalFinanceManagement/goals/delete';

  constructor(
    private srvApi: MEAPIService,
    private cache: CacheService,
  ) { }

  public getTips(): AsyncSubject<FinanceTips> {

    if (this.cache.get(CacheKey.PFM_TIPS)) {
      let ret = new AsyncSubject<FinanceTips>();
      ret.next(this.cache.get(CacheKey.PFM_TIPS));
      ret.complete();
      return ret;
    }

    let ret = new AsyncSubject<FinanceTips>();

    let cacheRet = new AsyncSubject<FinanceTips>();
    this.srvApi.Get<FinanceTips>(this.API_PFM_GET_TIPS, [new BasicAsyncResponseSuccessHandler<FinanceTips>(cacheRet)]);
    cacheRet.subscribe((tip) => {
      // tip = MOCK_TIPS;
      this.cache.set(CacheKey.PFM_TIPS, tip);
      ret.next(this.cache.get(CacheKey.PFM_TIPS));
      ret.complete();
    });
    return ret;
  }

  getGoals(): AsyncSubject<Goal[]> {
    let ret = new AsyncSubject<Goal[]>();

    this.srvApi.Post<Goal[]>(this.API_PFM_GET_GOALS, {}, [new BasicAsyncResponseSuccessHandler<Goal[]>(ret)]);

    return ret;
  }

  public deleteGoal(goal: Goal) : AsyncSubject<boolean> {    
    let bean = {
      id: goal.id
    }
    return this.srvApi.BasicPost(this.API_PFM_DELETE_GOAL, bean, [new MEDoNothingResponseHandler(ResponseCode.SUCCESS)])
  }
}


const MOCK_TIPS: FinanceTips = {
  "name": "John Doe",
  "tips": [  
  {  
    "subject": "Monitor Chequing Account Balance",  
    "message": "Maintain a balance of at least $500 to avoid overdraft fees and ensure smooth transactions.",
    "action" : []  
  },  
  {  
    "subject": "Identify and Avoid Recurring Fees",  
    "message": "Review transaction history to identify recurring fees and consider adjusting your spending habits or negotiating with service providers to reduce or eliminate these charges.",
    "action" : []    
  },  
  {  
    "subject": "Consider Chequing Account Upgrades",  
    "message": "Evaluate your chequing account features and benefits to determine if an upgrade is necessary to meet your financial needs and goals." ,
    "action" : []  
  },  
  {  
    "subject": "Prioritize Savings and Emergency Funds",  
    "message": "Allocate a portion of your income towards savings and emergency funds to ensure financial stability and security." ,
    "action" : []  
  }  
],"charts":[
  {
    "title": "Bank Fees",
    "summary": "The account has incurred significant fees over the past 6 months, including a $2.50 fee on 2025-06-03 and a $5.00 fee on 2025-06-03, with a total of $7.50 in bank fees in June 2025. Additionally, there were fees of $2.50 on 2025-02-06 and $15.00 on 2025-02-06, with a total of $17.50 in bank fees in February 2025. The account has also incurred fees of $2.50 on multiple occasions.",
    "series": [
      {
        "name": "June 2025",
        "value": 7.5
      },
      {
        "name": "May 2025",
        "value": 9.5
      },
      {
        "name": "April 2025",
        "value": 10.5
      },
      {
        "name": "March 2025",
        "value": 6.5
      },
      {
        "name": "February 2025",
        "value": 17.5
      },
      {
        "name": "January 2025",
        "value": 7.5
      }

    ],
    "config": {
      "legend": false,
      "xAxis": true,
      "yAxis": true,
      "showXAxisLabel": true,
      "showYAxisLabel": true,
      "xAxisLabel": "Date",
      "yAxisLabel": "Amount"
    }
  },
  {
    "title": "Regular Bill Payments",
    "summary": "The account has made regular bill payments of $0.00 in March 2025, $0.00 in April 2025, and $0.00 in May 2025, with a total of $0.00 in bill payments over the past 6 months.",
    "series": [
      {
        "name": "June 2025",
        "value": 7.5
      },
      {
        "name": "May 2025",
        "value": 9.5
      },
      {
        "name": "April 2025",
        "value": 10.5
      },
      {
        "name": "March 2025",
        "value": 6.5
      },
      {
        "name": "February 2025",
        "value": 17.5
      },
      {
        "name": "January 2025",
        "value": 7.5
      }
    ],
    "config": {
      "legend": false,
      "xAxis": true,
      "yAxis": true,
      "showXAxisLabel": true,
      "showYAxisLabel": true,
      "xAxisLabel": "Date",
      "yAxisLabel": "Amount"
    }
  },
  {
    "title": "One-time Payments or Withdrawals",
    "summary": "The account has made one-time payments or withdrawals of $0.00 in January 2025, $0.00 in March 2025, $0.00 in April 2025, and $0.00 in May 2025, with a total of $0.00 in one-time payments or withdrawals over the past 6 months.",
    "series": [
      {
        "name": "June 2025",
        "value": 7.5
      },
      {
        "name": "May 2025",
        "value": 9.5
      },
      {
        "name": "April 2025",
        "value": 10.5
      },
      {
        "name": "March 2025",
        "value": 6.5
      },
      {
        "name": "February 2025",
        "value": 17.5
      },
      {
        "name": "January 2025",
        "value": 7.5
      }
    ],
    "config": {
      "legend": false,
      "xAxis": true,
      "yAxis": true,
      "showXAxisLabel": true,
      "showYAxisLabel": true,
      "xAxisLabel": "Date",
      "yAxisLabel": "Amount"
    }
  }
]
}